import React from 'react';
import { Link } from 'react-router-dom';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    
    // Report error to API (safely)
    try {
      const apiBase = import.meta.env.VITE_API_BASE || '';
      if (apiBase) {
        fetch(`${apiBase.replace(/\/+$/, '')}/errors`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'react_crash',
            message: error.message,
            stack: error.stack,
            component_stack: errorInfo.componentStack,
            url: window.location.href,
            timestamp: new Date().toISOString(),
          }),
        }).catch(() => {}); // Silent fail
      }
    } catch {}
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Check if it's a map error
      const isMapError = this.state.error?.message?.includes('.map is not a function');
      
      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              حدث خطأ غير متوقع
            </h2>
            <p className="text-gray-500 mb-2">
              {isMapError 
                ? 'خطأ في تحميل البيانات. قد تكون البيانات بصيغة غير متوقعة.'
                : 'نعتذر عن هذا الخطأ. يرجى إعادة تحميل الصفحة.'
              }
            </p>
            {this.state.error?.message && (
              <p className="text-xs text-gray-400 mb-4 font-mono bg-gray-50 p-2 rounded overflow-auto">
                {this.state.error.message.slice(0, 200)}
              </p>
            )}
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={this.handleReload}
                className="btn btn-primary"
              >
                إعادة تحميل
              </button>
              <button
                onClick={this.handleReset}
                className="btn btn-secondary"
              >
                حاول مرة أخرى
              </button>
              <Link
                to="/pipeline"
                className="btn btn-secondary"
              >
                العودة للبايبلاين
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
